// utils/tokenManager.ts - Enhanced version with automatic app domain redirect

import { API_URLS } from '../api/urls';
import { SubdomainManager } from './subdomainUtils';

interface JWTPayload {
  sub: string;
  exp: number;
  type: "access" | "refresh";
  workspace_id?: number;
  user_id?: number;
  email?: string;
}

interface UserProfile {
  id: number;
  email: string;
  full_name: string;
  profile_image: string | null;
  onboarded: boolean;
  workspace_id?: number;
  workspace?: {
    id: number;
    name: string;
    subdomain?: string;
    icon_url?: string;
  };
}

interface WorkspaceData {
  id: number;
  name: string;
  subdomain?: string;
  icon_url?: string;
}

interface DeveloperCredentials {
  token: string;
  email: string;
  workspaceId?: number;
}

export class EnhancedTokenManager {
  private static ACCESS_TOKEN_KEY = "access_token";
  private static REFRESH_TOKEN_KEY = "refresh_token";
  private static DEVELOPER_CREDENTIALS_KEY = "developer_credentials";

  // Cache for decoded JWT
  private static decodedTokenCache: {
    token: string;
    payload: JWTPayload;
    timestamp: number;
  } | null = null;
  
  private static CACHE_DURATION = 10000;

  // Cache for frequently accessed values
  private static cachedUserId: number | null = null;
  private static cachedWorkspaceId: number | null = null;
  private static cachedEmail: string | null = null;
  private static valueCacheTimestamp: number = 0;
  private static VALUE_CACHE_TTL = 20000;

  private static getUserSpecificKey(baseKey: string, userId: number): string {
    return `${baseKey}_user_${userId}`;
  }

  private static isValueCacheValid(): boolean {
    return Date.now() - this.valueCacheTimestamp < this.VALUE_CACHE_TTL;
  }

  private static clearAllCaches() {
    this.decodedTokenCache = null;
    this.cachedUserId = null;
    this.cachedWorkspaceId = null;
    this.cachedEmail = null;
    this.valueCacheTimestamp = 0;
  }

  static decodeJWT(token: string): JWTPayload | null {
    try {
      const now = Date.now();
      if (
        this.decodedTokenCache &&
        this.decodedTokenCache.token === token &&
        now - this.decodedTokenCache.timestamp < this.CACHE_DURATION
      ) {
        return this.decodedTokenCache.payload;
      }

      const parts = token.split(".");
      if (parts.length !== 3) {
        console.warn("Invalid JWT format");
        return null;
      }

      const payload = parts[1];
      const paddedPayload = payload + "=".repeat((4 - (payload.length % 4)) % 4);
      const decoded = JSON.parse(atob(paddedPayload));
      
      if (process.env.NODE_ENV === 'development') {
        console.log("✅ JWT decoded and cached:", decoded);
      }
      
      this.decodedTokenCache = {
        token,
        payload: decoded,
        timestamp: now,
      };
      
      return decoded;
    } catch (error) {
      console.error("Error decoding JWT:", error);
      return null;
    }
  }

  static setDeveloperCredentials(credentials: DeveloperCredentials) {
    try {
      localStorage.setItem(this.DEVELOPER_CREDENTIALS_KEY, JSON.stringify(credentials));
      console.log("Developer credentials stored");
    } catch (error) {
      console.warn("Could not store developer credentials:", error);
      (window as any).__feebdack_developer_creds = credentials;
    }
  }

  static getDeveloperCredentials(): DeveloperCredentials | null {
    try {
      const stored = localStorage.getItem(this.DEVELOPER_CREDENTIALS_KEY);
      if (stored && stored !== "null") {
        return JSON.parse(stored);
      }
      return (window as any).__feebdack_developer_creds || null;
    } catch (error) {
      console.error("Error retrieving developer credentials:", error);
      return null;
    }
  }

  static clearDeveloperCredentials() {
    try {
      localStorage.removeItem(this.DEVELOPER_CREDENTIALS_KEY);
      delete (window as any).__feebdack_developer_creds;
      console.log("Developer credentials cleared");
    } catch (error) {
      console.warn("Error clearing developer credentials:", error);
    }
  }

  static setTokens(tokens: {
    access_token: string;
    refresh_token: string;
    token_type: string;
  }) {
    try {
      this.clearAllCaches();
      
      localStorage.setItem(this.ACCESS_TOKEN_KEY, tokens.access_token);
      localStorage.setItem(this.REFRESH_TOKEN_KEY, tokens.refresh_token);

      const payload = this.decodeJWT(tokens.access_token);
      if (payload && payload.sub) {
        const userId = parseInt(payload.sub);

        if (!isNaN(userId)) {
          localStorage.setItem("current_user_id", userId.toString());
          console.log("Current user ID set:", userId);

          if (payload.workspace_id) {
            this.setWorkspaceIdForUser(userId, payload.workspace_id);
          }
        }
      }

      console.log("Auth tokens stored successfully");
    } catch (error) {
      console.warn("Could not store tokens in localStorage:", error);
      (window as any).__feebdack_tokens = tokens;
    }
  }

  static getCurrentUserId(): number | null {
    if (this.isValueCacheValid() && this.cachedUserId !== null) {
      return this.cachedUserId;
    }

    try {
      const currentUserId = localStorage.getItem("current_user_id");
      if (currentUserId) {
        const parsed = parseInt(currentUserId);
        if (!isNaN(parsed)) {
          this.cachedUserId = parsed;
          this.valueCacheTimestamp = Date.now();
          return parsed;
        }
      }

      const accessToken = this.getAccessToken();
      if (accessToken) {
        const payload = this.decodeJWT(accessToken);
        if (payload && payload.sub) {
          const userId = parseInt(payload.sub);
          if (!isNaN(userId)) {
            localStorage.setItem("current_user_id", userId.toString());
            this.cachedUserId = userId;
            this.valueCacheTimestamp = Date.now();
            return userId;
          }
        }
      }

      return null;
    } catch (error) {
      console.error("Error getting current user ID:", error);
      return null;
    }
  }

  static getAccessToken(): string | null {
    try {
      const devCredentials = this.getDeveloperCredentials();
      if (devCredentials && devCredentials.token) {
        console.log("Using developer token for API calls");
        return devCredentials.token;
      }

      return (
        localStorage.getItem(this.ACCESS_TOKEN_KEY) ||
        (window as any).__feebdack_tokens?.access_token ||
        null
      );
    } catch (error) {
      console.warn("Could not retrieve access token:", error);
      return (window as any).__feebdack_tokens?.access_token || null;
    }
  }

  static getRefreshToken(): string | null {
    try {
      return (
        localStorage.getItem(this.REFRESH_TOKEN_KEY) ||
        (window as any).__feebdack_tokens?.refresh_token ||
        null
      );
    } catch (error) {
      console.warn("Could not retrieve refresh token:", error);
      return (window as any).__feebdack_tokens?.refresh_token || null;
    }
  }

  static getCurrentUserEmail(): string | null {
    if (this.isValueCacheValid() && this.cachedEmail !== null) {
      return this.cachedEmail;
    }

    try {
      const currentUserId = localStorage.getItem("current_user_id");
      
      if (currentUserId) {
        const userProfileKey = `user_profile_user_${currentUserId}`;
        const storedProfile = localStorage.getItem(userProfileKey);
        
        if (storedProfile && storedProfile !== "null") {
          const parsed = JSON.parse(storedProfile);
          if (parsed.email) {
            this.cachedEmail = parsed.email;
            this.valueCacheTimestamp = Date.now();
            return parsed.email;
          }
        }
      }

      const accessToken = this.getAccessToken();
      if (accessToken) {
        try {
          const payload = this.decodeJWT(accessToken);
          
          if (payload && payload.email) {
            this.cachedEmail = payload.email;
            this.valueCacheTimestamp = Date.now();
            return payload.email;
          }
        } catch (jwtError) {
          console.warn("Could not decode JWT for email:", jwtError);
        }
      }

      const memoryProfile = (window as any).__feebdack_profile;
      if (memoryProfile && memoryProfile.email) {
        this.cachedEmail = memoryProfile.email;
        this.valueCacheTimestamp = Date.now();
        return memoryProfile.email;
      }

      console.warn("❌ Could not retrieve user email from any source");
      return null;
    } catch (error) {
      console.error("❌ Error retrieving user email:", error);
      return null;
    }
  }

  static setWorkspaceIdForUser(userId: number, workspaceId: number) {
    try {
      const key = this.getUserSpecificKey("workspace_id", userId);
      localStorage.setItem(key, workspaceId.toString());
      
      this.cachedWorkspaceId = workspaceId;
      this.valueCacheTimestamp = Date.now();
      
      console.log(`Workspace ID ${workspaceId} stored for user ${userId}`);
    } catch (error) {
      console.warn("Could not store workspace ID:", error);
      (window as any)[`__feebdack_workspace_${userId}`] = workspaceId;
    }
  }

  static getWorkspaceIdForCurrentUser(): number | null {
    if (this.isValueCacheValid() && this.cachedWorkspaceId !== null) {
      return this.cachedWorkspaceId;
    }

    const devCredentials = this.getDeveloperCredentials();
    if (devCredentials && devCredentials.workspaceId) {
      this.cachedWorkspaceId = devCredentials.workspaceId;
      this.valueCacheTimestamp = Date.now();
      return devCredentials.workspaceId;
    }

    const userId = this.getCurrentUserId();
    if (!userId) {
      console.warn("No current user ID available");
      return null;
    }

    try {
      const key = this.getUserSpecificKey("workspace_id", userId);
      const storedId = localStorage.getItem(key);

      if (storedId && storedId !== "null" && storedId !== "undefined") {
        const parsedId = parseInt(storedId, 10);
        if (!isNaN(parsedId) && parsedId > 0) {
          this.cachedWorkspaceId = parsedId;
          this.valueCacheTimestamp = Date.now();
          console.log(`Retrieved workspace ID ${parsedId} for user ${userId}`);
          return parsedId;
        }
      }

      const memoryId = (window as any)[`__feebdack_workspace_${userId}`];
      if (memoryId && !isNaN(memoryId) && memoryId > 0) {
        const workspaceId = parseInt(memoryId.toString(), 10);
        this.cachedWorkspaceId = workspaceId;
        this.valueCacheTimestamp = Date.now();
        return workspaceId;
      }

      const accessToken = this.getAccessToken();
      if (accessToken) {
        const payload = this.decodeJWT(accessToken);
        if (payload && payload.workspace_id) {
          this.setWorkspaceIdForUser(userId, payload.workspace_id);
          return payload.workspace_id;
        }
      }

      console.log(`No workspace ID found for user ${userId}`);
      return null;
    } catch (error) {
      console.error("Error retrieving workspace ID:", error);
      return null;
    }
  }

  static getWorkspaceId(): number | null {
    return this.getWorkspaceIdForCurrentUser();
  }

  static setUserProfile(profile: UserProfile) {
    const userId = profile.id;

    try {
      const key = this.getUserSpecificKey("user_profile", userId);
      localStorage.setItem(key, JSON.stringify(profile));

      localStorage.setItem("current_user_id", userId.toString());

      if (profile.workspace) {
        this.setWorkspaceDataForUser(userId, profile.workspace);
      } else if (profile.workspace_id) {
        this.setWorkspaceIdForUser(userId, profile.workspace_id);
      }

      this.cachedUserId = userId;
      if (profile.email) this.cachedEmail = profile.email;
      if (profile.workspace_id) this.cachedWorkspaceId = profile.workspace_id;
      this.valueCacheTimestamp = Date.now();

      console.log(`User profile stored for user ${userId}:`, profile);
    } catch (error) {
      console.warn("Could not store user profile:", error);
      (window as any)[`__feebdack_profile_${userId}`] = profile;
    }
  }

  static getUserProfile(): UserProfile | null {
    const userId = this.getCurrentUserId();
    if (!userId) return null;

    try {
      const key = this.getUserSpecificKey("user_profile", userId);
      const stored = localStorage.getItem(key);
      if (stored && stored !== "null") {
        return JSON.parse(stored);
      }

      return (window as any)[`__feebdack_profile_${userId}`] || null;
    } catch (error) {
      console.error("Error retrieving user profile:", error);
      return null;
    }
  }

  static setWorkspaceDataForUser(userId: number, workspaceData: WorkspaceData) {
    try {
      const key = this.getUserSpecificKey("workspace_data", userId);
      localStorage.setItem(key, JSON.stringify(workspaceData));
      this.setWorkspaceIdForUser(userId, workspaceData.id);

      if (workspaceData.icon_url) {
        this.setWorkspaceIconForUser(userId, workspaceData.icon_url);
      }

      if (workspaceData.subdomain) {
        const extractedSubdomain = SubdomainManager.extractSubdomainFromUrl(workspaceData.subdomain);
        if (extractedSubdomain) {
          SubdomainManager.storeWorkspaceSubdomain(extractedSubdomain);
          console.log(`✅ Subdomain stored: ${extractedSubdomain}`);
        }
      }

      console.log(`Workspace data stored for user ${userId}:`, workspaceData);
    } catch (error) {
      console.warn("Could not store workspace data:", error);
      (window as any)[`__feebdack_workspace_data_${userId}`] = workspaceData;
      this.setWorkspaceIdForUser(userId, workspaceData.id);

      if (workspaceData.icon_url) {
        this.setWorkspaceIconForUser(userId, workspaceData.icon_url);
      }

      if (workspaceData.subdomain) {
        const extractedSubdomain = SubdomainManager.extractSubdomainFromUrl(workspaceData.subdomain);
        if (extractedSubdomain) {
          SubdomainManager.storeWorkspaceSubdomain(extractedSubdomain);
        }
      }
    }
  }

  static getWorkspaceDataForCurrentUser(): WorkspaceData | null {
    const userId = this.getCurrentUserId();
    if (!userId) return null;

    try {
      const key = this.getUserSpecificKey("workspace_data", userId);
      const storedData = localStorage.getItem(key);

      if (storedData && storedData !== "null" && storedData !== "undefined") {
        const parsed = JSON.parse(storedData);
        if (parsed && parsed.id && parsed.name && typeof parsed.id === "number") {
          return parsed;
        }
      }

      const memoryData = (window as any)[`__feebdack_workspace_data_${userId}`];
      if (memoryData && memoryData.id && memoryData.name) {
        return memoryData;
      }

      return null;
    } catch (error) {
      console.error("Error retrieving workspace data:", error);
      return null;
    }
  }

  static setWorkspaceIconForUser(userId: number, iconUrl: string) {
    try {
      const key = this.getUserSpecificKey("workspace_icon", userId);
      localStorage.setItem(key, iconUrl);
      localStorage.setItem("workspace_icon_url", iconUrl);
      console.log(`Workspace icon stored for user ${userId}:`, iconUrl);
    } catch (error) {
      console.warn("Could not store workspace icon:", error);
      (window as any)[`__feebdack_workspace_icon_${userId}`] = iconUrl;
    }
  }

  static getWorkspaceIconForCurrentUser(): string | null {
    const userId = this.getCurrentUserId();
    if (!userId) return null;

    try {
      const key = this.getUserSpecificKey("workspace_icon", userId);
      const storedIcon = localStorage.getItem(key);

      if (storedIcon && storedIcon !== "null" && storedIcon !== "undefined") {
        return storedIcon;
      }

      const workspaceData = this.getWorkspaceDataForCurrentUser();
      if (workspaceData && workspaceData.icon_url) {
        this.setWorkspaceIconForUser(userId, workspaceData.icon_url);
        return workspaceData.icon_url;
      }

      const userProfile = this.getUserProfile();
      if (userProfile && userProfile.workspace && userProfile.workspace.icon_url) {
        this.setWorkspaceIconForUser(userId, userProfile.workspace.icon_url);
        return userProfile.workspace.icon_url;
      }

      const memoryIcon = (window as any)[`__feebdack_workspace_icon_${userId}`];
      if (memoryIcon) {
        return memoryIcon;
      }

      const legacyIcon = localStorage.getItem("workspace_icon_url");
      if (legacyIcon && legacyIcon !== "null") {
        return legacyIcon;
      }

      return null;
    } catch (error) {
      console.error("Error retrieving workspace icon:", error);
      return null;
    }
  }

  static setOnboardingWorkspaceData(onboardingData: any) {
    const userId = this.getCurrentUserId();
    if (!userId) {
      console.warn("No current user ID available");
      return;
    }

    if (onboardingData && onboardingData.workspace) {
      const workspaceData: WorkspaceData = {
        id: onboardingData.workspace.id,
        name: onboardingData.workspace.name,
        subdomain: onboardingData.workspace.subdomain,
        icon_url: onboardingData.icon_url || onboardingData.workspace.icon_url,
      };

      this.setWorkspaceDataForUser(userId, workspaceData);

      if (workspaceData.subdomain) {
        const extractedSubdomain = SubdomainManager.extractSubdomainFromUrl(workspaceData.subdomain);
        if (extractedSubdomain) {
          SubdomainManager.storeWorkspaceSubdomain(extractedSubdomain);
          console.log(`✅ Onboarding subdomain stored: ${extractedSubdomain}`);
        }
      }

      if (onboardingData.user_id) {
        const existingProfile = this.getUserProfile();
        if (existingProfile) {
          const updatedProfile: UserProfile = {
            ...existingProfile,
            workspace_id: workspaceData.id,
            workspace: workspaceData,
            onboarded: true,
          };
          this.setUserProfile(updatedProfile);
        }
      }
    }
  }

  static getWorkspaceData() {
    return this.getWorkspaceDataForCurrentUser();
  }

  static async fetchAndCheckUserProfile(accessToken?: string): Promise<{
    success: boolean;
    profile?: UserProfile;
    needsOnboarding: boolean;
    error?: string;
  }> {
    try {
      const token = accessToken || this.getAccessToken();
      if (!token) {
        return {
          success: false,
          needsOnboarding: true,
          error: "No access token available"
        };
      }

      console.log("Fetching user profile from API...");

      const response = await fetch(API_URLS.GET_USER_PROFILE, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        console.error(`Failed to fetch user profile: ${response.status}`);
        
        // ✅ NEW: On 401, trigger redirect if on subdomain
        if (response.status === 401) {
          this.handleAuthenticationFailure();
        }
        
        return {
          success: false,
          needsOnboarding: true,
          error: `API error: ${response.status}`
        };
      }

      const userProfile: UserProfile = await response.json();
      console.log("User profile fetched successfully:", userProfile);

      this.setUserProfile(userProfile);

      if (userProfile.workspace) {
        this.setWorkspaceDataForUser(userProfile.id, userProfile.workspace);
      } else if (userProfile.workspace_id) {
        this.setWorkspaceIdForUser(userProfile.id, userProfile.workspace_id);
      }

      return {
        success: true,
        profile: userProfile,
        needsOnboarding: !userProfile.onboarded
      };

    } catch (error) {
      console.error("Error fetching user profile:", error);
      return {
        success: false,
        needsOnboarding: true,
        error: error instanceof Error ? error.message : "Network error"
      };
    }
  }

  static async checkUserOnboardingStatus(): Promise<{
    hasWorkspace: boolean;
    workspaceId?: number;
    needsOnboarding: boolean;
    isOnboarded: boolean;
    error?: string;
  }> {
    try {
      const result = await this.fetchAndCheckUserProfile();
      
      if (result.success && result.profile) {
        const profile = result.profile;
        const hasWorkspace = !!(profile.workspace_id || profile.workspace?.id);
        const workspaceId = profile.workspace_id || profile.workspace?.id;

        return {
          hasWorkspace,
          workspaceId,
          needsOnboarding: !profile.onboarded,
          isOnboarded: profile.onboarded,
        };
      } else {
        const accessToken = this.getAccessToken();
        if (accessToken) {
          try {
            const payload = this.decodeJWT(accessToken);
            if (payload && payload.workspace_id && payload.workspace_id > 0) {
              const userId = this.getCurrentUserId();
              if (userId) {
                this.setWorkspaceIdForUser(userId, payload.workspace_id);
              }
              
              return {
                hasWorkspace: true,
                workspaceId: payload.workspace_id,
                needsOnboarding: false,
                isOnboarded: true,
              };
            }
          } catch (jwtError) {
            console.warn("Could not decode JWT:", jwtError);
          }
        }

        return {
          hasWorkspace: false,
          needsOnboarding: true,
          isOnboarded: false,
          error: result.error || "Could not determine onboarding status"
        };
      }
    } catch (error) {
      console.error("Error checking onboarding status:", error);
      return {
        hasWorkspace: false,
        needsOnboarding: true,
        isOnboarded: false,
        error: "Unknown error occurred"
      };
    }
  }

  static async isReturningUserAsync(): Promise<boolean> {
    const status = await this.checkUserOnboardingStatus();
    return status.isOnboarded && status.hasWorkspace;
  }

  static isReturningUser(): boolean {
    const devCredentials = this.getDeveloperCredentials();
    if (devCredentials) {
      return true;
    }

    const userId = this.getCurrentUserId();
    if (!userId) return false;

    try {
      const userProfile = this.getUserProfile();
      if (userProfile && userProfile.onboarded) {
        console.log("User profile indicates onboarded user");
        return true;
      }

      const accessToken = this.getAccessToken();
      if (accessToken) {
        try {
          const payload = this.decodeJWT(accessToken);
          if (payload && payload.workspace_id && payload.workspace_id > 0) {
            console.log("User has workspace in JWT token");
            return true;
          }
        } catch (jwtError) {
          console.warn("Could not decode JWT:", jwtError);
        }
      }

      const workspaceId = this.getWorkspaceIdForCurrentUser();
      if (workspaceId && workspaceId > 0) {
        console.log("User has stored workspace data");
        return true;
      }

      console.log("No onboarding indicators found for user");
      return false;
    } catch (error) {
      console.error("Error checking if returning user:", error);
      return false;
    }
  }

  static async determinePostAuthRoute(email: string, fullName?: string, isSignUp: boolean = false): Promise<{
    route: string;
    state?: any;
  }> {
    try {
      console.log("Determining post-auth route for user...");
      
      if (isSignUp) {
        console.log("New signup detected, routing to onboarding");
        return {
          route: "/onboarding",
          state: { email, fullName, fromSignUp: true }
        };
      }

      const profileResult = await this.fetchAndCheckUserProfile();
      
      if (profileResult.success && profileResult.profile) {
        if (profileResult.profile.onboarded) {
          console.log("User is onboarded, routing to dashboard");
          return { route: "/dashboard" };
        } else {
          console.log("User is not onboarded, routing to onboarding");
          return {
            route: "/onboarding",
            state: {
              email: profileResult.profile.email,
              fullName: profileResult.profile.full_name,
              fromSignIn: true
            }
          };
        }
      } else {
        const onboardingStatus = await this.checkUserOnboardingStatus();
        
        if (onboardingStatus.isOnboarded && onboardingStatus.hasWorkspace) {
          console.log("JWT indicates onboarded user, routing to dashboard");
          return { route: "/dashboard" };
        } else {
          console.log("Cannot determine status, defaulting to onboarding");
          return {
            route: "/onboarding",
            state: {
              email,
              fullName,
              fromSignIn: true,
              error: profileResult.error || "Could not verify account status"
            }
          };
        }
      }
    } catch (error) {
      console.error("Error determining post-auth route:", error);
      return {
        route: "/onboarding",
        state: {
          email,
          fullName,
          fromSignIn: true,
          error: "Could not verify account status"
        }
      };
    }
  }

  static hasCompleteSetup(): boolean {
    const devCredentials = this.getDeveloperCredentials();
    if (devCredentials) {
      return true;
    }

    const hasTokens = !!(this.getAccessToken() && this.getRefreshToken());
    const userProfile = this.getUserProfile();
    const hasWorkspace = !!this.getWorkspaceIdForCurrentUser();
    const userId = this.getCurrentUserId();

    console.log("Setup check:", { 
      hasTokens, 
      hasWorkspace, 
      userId,
      isOnboarded: userProfile?.onboarded
    });

    return hasTokens && hasWorkspace && !!userId && (userProfile?.onboarded || false);
  }

  /**
   * ✅ NEW: Handle authentication failures - redirect to app domain
   */
  private static handleAuthenticationFailure() {
    const isOnSubdomain = SubdomainManager.isOnWorkspaceSubdomain();
    
    if (isOnSubdomain && !SubdomainManager.isLocalDevelopment()) {
      console.log("🚫 Authentication failed on subdomain, redirecting to app domain");
      SubdomainManager.navigateToAppDomain('/', true);
    } else {
      this.performLogout(false);
    }
  }

  /**
   * ✅ UPDATED: Refresh token with automatic redirect on failure
   */
  static async refreshAccessToken(): Promise<boolean> {
    const refreshToken = this.getRefreshToken();

    if (!refreshToken) {
      console.error("No refresh token available");
      return false;
    }

    try {
      console.log("🔄 Refreshing access token...");

      const response = await fetch(API_URLS.REFRESH_TOKEN, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          refresh_token: refreshToken,
        }),
      });

      if (!response.ok) {
        console.error("Token refresh failed:", response.status);

        // Only force logout when the server explicitly rejects the refresh token.
        // Any other status (500, network error, etc.) should not log the user out —
        // they may be able to retry once connectivity is restored.
        if (response.status === 401) {
          console.warn("Refresh token invalid, logging out");
          this.handleAuthenticationFailure();
        }

        return false;
      }

      const data = await response.json();

      this.setTokens({
        access_token: data.access_token,
        refresh_token: refreshToken,
        token_type: data.token_type || "bearer",
      });

      console.log("✅ Access token refreshed successfully");
      return true;

    } catch (error) {
      // Network error (offline, timeout, DNS) — do NOT log the user out.
      // Tokens remain in localStorage so the next successful request will work.
      console.error("Token refresh network error (session preserved):", error);
      return false;
    }
  }

  static isTokenExpired(): boolean {
    const accessToken = this.getAccessToken();
    if (!accessToken) return true;

    try {
      const payload = this.decodeJWT(accessToken);
      if (!payload || !payload.exp) return true;

      const now = Math.floor(Date.now() / 1000);
      const timeUntilExpiry = payload.exp - now;
      
      return timeUntilExpiry < 7200;
    } catch (error) {
      return true;
    }
  }

  static async ensureValidToken(): Promise<boolean> {
    if (this.isTokenExpired()) {
      console.log("⚠️ Token expired or expiring soon, refreshing...");
      return await this.refreshAccessToken();
    }
    return true;
  }

  /**
   * ✅ UPDATED: Enhanced logout with subdomain redirect
   */
  static performLogout(redirectToAppDomain: boolean = false) {
    try {
      const userId = this.getCurrentUserId();
      const isOnSubdomain = SubdomainManager.isOnWorkspaceSubdomain();

      this.clearAllCaches();

      localStorage.removeItem(this.ACCESS_TOKEN_KEY);
      localStorage.removeItem(this.REFRESH_TOKEN_KEY);
      localStorage.removeItem("current_user_id");

      this.clearDeveloperCredentials();

      SubdomainManager.clearWorkspaceSubdomain();

      delete (window as any).__feebdack_tokens;

      console.log(`Logout completed for user ${userId}`);

      if ((isOnSubdomain || redirectToAppDomain) && !SubdomainManager.isLocalDevelopment()) {
        console.log("🔄 Redirecting to app domain after logout");
        setTimeout(() => {
          SubdomainManager.navigateToAppDomain('/', true);
        }, 100);
      }

      return true;
    } catch (error) {
      console.error("Error during logout:", error);
      return false;
    }
  }

  /**
   * ✅ NEW: Public method for manual logout with redirect
   */
  static logout() {
    this.performLogout(true);
  }

  static clearAllData() {
    try {
      this.clearAllCaches();

      const keys = Object.keys(localStorage);

      keys.forEach((key) => {
        if (
          key.includes("workspace_id_user_") ||
          key.includes("user_profile_user_") ||
          key.includes("workspace_data_user_") ||
          key.includes("workspace_icon_user_") ||
          key === "current_user_id" ||
          key === "workspace_icon_url" ||
          key === this.ACCESS_TOKEN_KEY ||
          key === this.REFRESH_TOKEN_KEY ||
          key === this.DEVELOPER_CREDENTIALS_KEY
        ) {
          localStorage.removeItem(key);
        }
      });

      Object.keys(window).forEach((key) => {
        if (key.includes("__feebdack_")) {
          delete (window as any)[key];
        }
      });

      console.log("All user data cleared");
      return true;
    } catch (error) {
      console.error("Error clearing all data:", error);
      return false;
    }
  }

  static debugUserData(): void {
    const userId = this.getCurrentUserId();
    const workspaceId = this.getWorkspaceIdForCurrentUser();
    const profile = this.getUserProfile();
    const workspaceData = this.getWorkspaceDataForCurrentUser();
    const workspaceIcon = this.getWorkspaceIconForCurrentUser();
    const devCredentials = this.getDeveloperCredentials();

    console.group("🔍 User Data Debug");
    console.log("Current User ID:", userId);
    console.log("Workspace ID:", workspaceId);
    console.log("User Profile:", profile);
    console.log("Is Onboarded:", profile?.onboarded);
    console.log("Workspace Data:", workspaceData);
    console.log("Workspace Icon:", workspaceIcon);
    console.log("Developer Credentials:", devCredentials ? "Present" : "None");
    console.log("Has Complete Setup:", this.hasCompleteSetup());
    console.log("Is Returning User:", this.isReturningUser());
    console.log("Cache Status:", {
      decodedToken: this.decodedTokenCache ? "Cached" : "Empty",
      userId: this.cachedUserId,
      workspaceId: this.cachedWorkspaceId,
      email: this.cachedEmail,
      cacheAge: this.valueCacheTimestamp ? `${Date.now() - this.valueCacheTimestamp}ms ago` : "N/A"
    });
    console.groupEnd();
  }

  static async fetchWithAutoRefresh(
    url: string,
    options: RequestInit = {}
  ): Promise<Response> {
    let accessToken = this.getAccessToken();
    
    if (!accessToken) {
      throw new Error("No access token available");
    }

    let response = await fetch(url, {
      ...options,
      headers: {
        ...options.headers,
        Authorization: `Bearer ${accessToken}`,
      },
    });

    if (response.status === 401) {
      console.log("🔄 Got 401, attempting token refresh...");
      
      const refreshed = await this.refreshAccessToken();
      
      if (refreshed) {
        accessToken = this.getAccessToken();
        response = await fetch(url, {
          ...options,
          headers: {
            ...options.headers,
            Authorization: `Bearer ${accessToken}`,
          },
        });
        
        console.log("✅ Request retried with refreshed token");
      } else {
        throw new Error("Session expired. Please sign in again.");
      }
    }

    return response;
  }
}

export async function fetchUserProfileAndWorkspace(): Promise<{
  success: boolean;
  data?: UserProfile;
  error?: string;
}> {
  try {
    const tokenValid = await EnhancedTokenManager.ensureValidToken();
    
    if (!tokenValid) {
      return {
        success: false,
        error: "Session expired. Please sign in again."
      };
    }

    console.log("Fetching user profile...");

    const response = await EnhancedTokenManager.fetchWithAutoRefresh(
      API_URLS.GET_USER_PROFILE,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      }
    );

    if (!response.ok) {
      console.error(`Failed to fetch user profile: ${response.status}`);
      return {
        success: false,
        error: `API error: ${response.status}`
      };
    }

    const userData: UserProfile = await response.json();
    console.log("✅ User profile fetched successfully");

    if (userData.onboarded && !userData.workspace_id && !userData.workspace) {
      console.log("Fetching workspace information...");
      
      try {
        const workspaceResponse = await EnhancedTokenManager.fetchWithAutoRefresh(
          API_URLS.GET_WORKSPACES,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
            },
          }
        );

        if (workspaceResponse.ok) {
          const workspaceData = await workspaceResponse.json();
          console.log("✅ Workspaces fetched:", workspaceData.workspaces?.length);
          
          if (workspaceData.workspaces && workspaceData.workspaces.length > 0) {
            const primaryWorkspace = workspaceData.workspaces[0];
            
            userData.workspace_id = primaryWorkspace.id;
            userData.workspace = {
              id: primaryWorkspace.id,
              name: primaryWorkspace.name,
              subdomain: primaryWorkspace.subdomain
            };
          }
        }
      } catch (workspaceError) {
        console.warn("Could not fetch workspaces:", workspaceError);
      }
    }

    EnhancedTokenManager.setUserProfile(userData);

    return { success: true, data: userData };
    
  } catch (error) {
    console.error("Error fetching user profile:", error);
    return {
      success: false,
      error: error instanceof Error ? error.message : "Network error",
    };
  }
}